(ns arthur.synth
  "Synthetic landmark frames, shaped exactly like FaceLandmarker output.

  Exists so the whole chain downstream of detection - Procrustes, smoothing,
  stabilisation, key selection, rasterising, take writing - can be exercised and
  verified without a video file. A synthetic face is also the only way to test
  stabilisation against a KNOWN head motion, since real footage gives no ground
  truth to compare against.

  In `src/` and not in `test/`, and that moved at step 5: the synthetic take is
  what the tool plays before any footage has been detected, so the generator is
  app code that stands in for `flow/detect` rather than a test fixture. Step 6
  puts MediaPipe beside it, not in place of it — a tool that needs a video file
  before it will show you anything is a tool you cannot debug.

  Differs from js/synth.js in exactly one way, deliberately: the jitter comes
  from a SEEDED generator rather than Math.random. A failing assertion has to be
  reproducible to be worth anything, and a wrong pose that happened once is not
  a bug report. `:rand-fn` takes the generator over entirely, which is what let
  the JS-parity harness hand both implementations the identical track while it
  existed."
  (:require [arthur.domain.landmarks :as lm]))

;; mulberry32. Chosen for being four lines of int32 arithmetic that port
;; unambiguously between JS and CLJS, not for its statistics: this is jitter for
;; a smoother to remove, not a source of entropy.
(defn mulberry32 [seed]
  (let [a (atom (bit-or seed 0))]
    (fn []
      (let [x (swap! a (fn [v] (bit-or (+ v 0x6D2B79F5) 0)))
            t (js/Math.imul (bit-xor x (unsigned-bit-shift-right x 15)) (bit-or 1 x))
            t (bit-xor (+ t (js/Math.imul (bit-xor t (unsigned-bit-shift-right t 7))
                                         (bit-or 61 t)))
                       t)]
        (/ (unsigned-bit-shift-right (bit-xor t (unsigned-bit-shift-right t 14)) 0)
           4294967296)))))

;; Half the corner separation, and the lid half-height at full open.
(def ^:private EYE-RX 0.0235)
(def ^:private EYE-RY 0.011)
(def ^:private EYE-Y -0.044)

(defn synth-dense
  "`n-frames` of dense landmarks.

  `:swap-iris` places the two iris blocks on the opposite eyes. It exists so the
  pairing resolver can be tested against a track it actually disagrees with:
  a resolver checked only against the convention it was written for is checking
  nothing at all."
  ([] (synth-dense 72 {}))
  ([n-frames] (synth-dense n-frames {}))
  ([n-frames {:keys [swap-iris rand-fn seed]
              :or   {swap-iris false, seed 1}}]
   (let [rnd (or rand-fn (mulberry32 seed))]
     (vec
      (for [t (range n-frames)]
        (let [pts (make-array lm/NUM-LANDMARKS)
              _   (dotimes [i lm/NUM-LANDMARKS] (aset pts i {:x 0.5 :y 0.5 :z 0}))

              ;; Known head motion: drift, sway, roll and a slow scale change, plus a
              ;; little per-frame jitter so transform smoothing has something to remove.
              ph    (/ t n-frames)
              hx    (+ 0.5 (* 0.045 (js/Math.sin (* ph js/Math.PI 2))) (* (- (rnd) 0.5) 0.002))
              hy    (+ 0.5 (* 0.02 (js/Math.cos (* ph js/Math.PI 3))) (* (- (rnd) 0.5) 0.002))
              roll  (* 0.18 (js/Math.sin (* ph js/Math.PI 2.5)))
              scale (+ 1 (* 0.06 (js/Math.sin (* ph js/Math.PI 1.5))))
              cr    (js/Math.cos roll)
              sr    (js/Math.sin roll)
              place (fn [i lx ly]
                      (let [sx (* lx scale) sy (* ly scale)]
                        (aset pts i {:x (- (+ hx (* cr sx)) (* sr sy))
                                     :y (+ hy (* sr sx) (* cr sy))
                                     :z 0})))

              ;; Mouth opens in four sustained beats with holds between, so key selection
              ;; has genuine extremes and genuine plateaux to find.
              beat     (mod (js/Math.floor (/ t 9)) 4)
              open-amt (nth [0.004 0.05 0.022 0.0] beat)
              wide     (+ 0.10 (case beat 1 0.012, 3 -0.008, 0))

              ;; A blink is ONE frame, which is the honest hard case: at 12fps that is
              ;; what a real blink costs, and it is exactly the length that reads as a
              ;; dropped frame rather than as a blink unless `hold` extends it.
              blink    (and (> t 5) (zero? (mod t 19)))
              openness (if blink 0.05 1)

              ;; Gaze holds and then jumps, the way gaze actually behaves, with a little
              ;; jitter on top so quantisation has noise to remove and the dwell has
              ;; something to suppress.
              [gx gy] (nth [[0 0] [0.16 0.0] [-0.16 0.05] [0.0 -0.09]]
                           (mod (js/Math.floor (/ t 11)) 4))
              jit     (fn [] (* (- (rnd) 0.5) 0.012))

              ;; Eyes. The corners (RIGID[0..3]) are placed BY the lid rings rather than
              ;; separately, because they are slots 0 and 8 of those rings: writing them
              ;; twice is how the mouth grew a bowtie, and a corner that disagrees with
              ;; its own ring would make the eye self-intersect at some vertex budgets
              ;; and not others.
              eye (fn [ring cx dir iris]
                    (let [n (count ring)]
                      (dotimes [k n]
                        ;; dir flips the traversal so each ring runs the direction its real
                        ;; table does: slot 0 outer corner, 4 upper lid, 8 inner, 12 lower.
                        (let [a (if (pos? dir)
                                  (+ js/Math.PI (* (/ k n) js/Math.PI 2))
                                  (- (* (/ k n) js/Math.PI 2)))]
                          (place (nth ring k)
                                 (+ cx (* EYE-RX (js/Math.cos a)))
                                 (+ EYE-Y (* EYE-RY openness (js/Math.sin a))))))
                      ;; Iris: centre first, then four ring points, as the refined mesh emits.
                      (let [ix (+ cx (* (+ gx (jit)) EYE-RX 2))
                            iy (+ EYE-Y (* (+ gy (jit)) EYE-RX 2))
                            m  (count iris)]
                        (place (nth iris 0) ix iy)
                        (doseq [k (range 1 m)]
                          (let [a (* (/ (dec k) (dec m)) js/Math.PI 2)]
                            (place (nth iris k)
                                   (+ ix (* 0.008 (js/Math.cos a)))
                                   (+ iy (* 0.008 (js/Math.sin a)))))))))

              ;; Brows, held in four sustained poses so raise quantisation has genuine
              ;; plateaux to find: rest, surprise (both ends up), worry (inner up only),
              ;; anger (inner down). Commanded in eye widths above the eye centre so the
              ;; measurement can be checked against a number rather than an eyeball.
              [b-out b-in] (nth [[0.30 0.30] [0.46 0.46] [0.30 0.44] [0.30 0.18]]
                                (mod (js/Math.floor (/ t 13)) 4))
              EYE-W (* EYE-RX 2)
              HALF  0.006                    ; ring half-thickness
              brow (fn [ring cx outer-sign]
                     ;; Slots 0-4 are one edge outer->inner, 5-9 the other inner->outer, so the
                     ;; ends land on {0,9} and {4,5} exactly as the table promises.
                     (let [n (count ring) half (/ n 2)]
                       (dotimes [k n]
                         (let [along (if (< k half)
                                       (/ k (dec half))
                                       (/ (- n 1 k) (dec half)))
                               rise  (+ b-out (* (- b-in b-out) along))]
                           (place (nth ring k)
                                  (+ cx (* outer-sign (- EYE-RX (* along EYE-W)) 1.05))
                                  (+ (- EYE-Y (* rise EYE-W))
                                     (if (< k half) (- HALF) HALF)))))))

              ;; Lip rings as ellipse arcs, traversed so ring ORDER matches the tables:
              ;; slot 0 = right corner, 5 = top centre, 10 = left corner, 15 = bottom
              ;; centre, with y growing downward. Getting this convention wrong swaps two
              ;; opposite vertices and the ring self-intersects into a bowtie - see the
              ;; ring-simplicity assertion in domain/ring's tests.
              ring (fn [table rx ry cy]
                     (let [n (count table)]
                       (dotimes [k n]
                         (let [a (- (* (/ k n) js/Math.PI 2))]
                           (place (nth table k)
                                  (* rx (js/Math.cos a))
                                  (+ cy (* ry (js/Math.sin a))))))))]

          (place (nth lm/RIGID 4) 0.000 -0.050)
          (place (nth lm/RIGID 5) 0.000 -0.020)
          (place (nth lm/RIGID 6) 0.000  0.012)

          (eye lm/EYE-R-RING -0.0515  1 (if swap-iris lm/IRIS-B lm/IRIS-A))
          (eye lm/EYE-L-RING  0.0515 -1 (if swap-iris lm/IRIS-A lm/IRIS-B))

          (brow lm/BROW-A-RING -0.0515 -1)
          (brow lm/BROW-B-RING  0.0515  1)

          (ring lm/LIPS-OUTER (/ wide 2) (+ 0.012 (* open-amt 0.6)) 0.075)
          ;; APERTURE (13, 14) are slots 5 and 15 of the inner ring, so the ring itself
          ;; places them at the vertical extremes. Writing them again afterwards is what
          ;; produced the bowtie; the aperture is simply the inner ring's height.
          (ring lm/LIPS-INNER (/ wide 2.6) (+ 0.001 open-amt) 0.075)

          (let [n (count lm/FACE-OVAL)]
            (dotimes [k n]
              (let [a (+ (- (/ js/Math.PI 2)) (* (/ k n) js/Math.PI 2))]
                (place (nth lm/FACE-OVAL k)
                       (* 0.105 (js/Math.cos a))
                       (+ (* 0.145 (js/Math.sin a)) 0.01)))))

          (vec pts)))))))
